import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  SectionDefinition,
  SectionDefinitionFilter,
  SortTypeSecDef,
  SectionDefinitionEntityType
} from '../../../../models/section-definition.model';
import { SectionDefinitionService } from '../../../../services/section-definition.service';
import { ToastrService } from 'ngx-toastr';
import { UtilitySharedService } from '../../../../services/shared//utility-shared.service';

@Component({
  selector: 'lib-reorder-section-definition',
  templateUrl: './reorder-section-definition.component.html',
  styleUrls: ['./reorder-section-definition.component.scss'],
  standalone: false,
})
export class ReorderSectionDefinitionComponent implements OnInit {
  constructor(
    private sectionDefinitionService: SectionDefinitionService,
    private toastr: ToastrService,
    private bpsUtilityService: UtilitySharedService
  ) {}

  @Output() closeModal = new EventEmitter<void>();
  EntityType = SectionDefinitionEntityType;

  allSectionDefinitions: SectionDefinition[] = [];
  filteredSections: SectionDefinition[] = [];

  entityTypeLabels: Partial<Record<SectionDefinitionEntityType, string>> = {
    [SectionDefinitionEntityType.Pole]: 'Pole',
    [SectionDefinitionEntityType.DistributionSubstation]: 'Distribution Substation',
    [SectionDefinitionEntityType.InjectionSubstation]: 'Injection Substation',
    [SectionDefinitionEntityType.TransmissionStation]: 'Transmission Station',
  };

  ngOnInit() {
    this.loadAllSections();
  }

  loadAllSections(): void {
    const filter = new SectionDefinitionFilter();
    filter.sortBy = SortTypeSecDef.DISPLAY_ORDER;
    const requestPayload = {
      pageInfo: {
        pageSize: 999999,
        page: 1,
      },
      filterParams: filter,
    };

    this.sectionDefinitionService.getAllForSelect(requestPayload).subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.allSectionDefinitions = responseData?.data?.data?.length
            ? responseData?.data?.data?.map(
                (el: any) => new SectionDefinition(el)
              )
            : [];

          if (responseData.message !== '') {
            this.toastr.warning(responseData.message);
          }

          this.updateFilteredSections();
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: () => this.toastr.error('Error occurred'),
    });
  }

  entityTypes: SectionDefinitionEntityType[] = Object.values(SectionDefinitionEntityType).filter(
    (v) => typeof v === 'number'
  ) as SectionDefinitionEntityType[];

  selectedEntityType: SectionDefinitionEntityType = SectionDefinitionEntityType.Pole;

  updateFilteredSections(): void {
    this.filteredSections = this.allSectionDefinitions.filter(
      (section) =>
        String(section.assetType) === SectionDefinitionEntityType[this.selectedEntityType]
    );
  }

  onEntityTypeSelect(type: SectionDefinitionEntityType): void {
    this.selectedEntityType = type;
    this.updateFilteredSections();
  }

  onDrop(event: CdkDragDrop<SectionDefinition[]>) {
    moveItemInArray(
      this.filteredSections,
      event.previousIndex,
      event.currentIndex
    );

    this.filteredSections.forEach((sectionDefinition, index) => {
      sectionDefinition.displayOrder = index;
    });

    const payload = this.filteredSections.map((s, i) => ({
      id: s.id,
      displayOrder: i,
    }));

    this.sectionDefinitionService.updateOrder(payload).subscribe({
      next: () => this.toastr.success('Order updated successfully'),
      error: (err) => this.toastr.error('Update failed'),
    });
  }

  close(): void {
    this.closeModal.emit();
  }

  trackById(_: number, section: SectionDefinition): number {
    return section.id;
  }

  onBackdropClick(): void {
    if (this.showDeleteModal) {
      this.cancelDelete();
    } else {
      this.close();
    }
  }

  showDeleteModal: boolean = false;
  itemToDelete: SectionDefinition | null = null;

  onDotsClick(item: SectionDefinition): void {
    this.itemToDelete = item;
    this.showDeleteModal = true;
  }

  confirmDelete(): void {
    if (!this.itemToDelete) return;

    this.sectionDefinitionService.delete(this.itemToDelete.id).subscribe({
      next: () => {
        this.toastr.success('Item deleted');
        this.allSectionDefinitions = this.allSectionDefinitions.filter(
          (s) => s.id !== this.itemToDelete!.id
        );
        this.filteredSections = this.filteredSections.filter(
          (s) => s.id !== this.itemToDelete!.id
        );
        this.itemToDelete = null;
        this.showDeleteModal = false;
      },
      error: () => this.toastr.error('Failed to delete item'),
    });
  }

  cancelDelete(): void {
    this.showDeleteModal = false;
    this.itemToDelete = null;
  }
}
